import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateTemplateAdditionalReceiverRequest,
  CreateTemplateMappingRequest,
  CreateTemplateRequest,
  Template,
  TemplateAdditionalReceiver,
  TemplateListItem,
  TemplateMapping,
  TemplateMappingContent,
  UpdateTemplateAdditionalReceiverRequest,
  UpdateTemplateMappingContentRequest,
  UpdateTemplateMappingRequest,
  UpdateTemplateRequest,
} from '@models/notifications/templates';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { TemplatesService } from './templates.service';

type TemplatesState = {
  templates: TemplateListItem[];
  isLoading: boolean;
  templateCreateLoading: boolean;

  selectedTemplate: Template | null;
  selectedTemplateLoading: boolean;

  additionalReceivers: TemplateAdditionalReceiver[];
  additionalReceiversLoading: boolean;
  selectedAdditionalReceiver: TemplateAdditionalReceiver | null;
  selectedAdditionalReceiverLoading: boolean;
  additionalReceiverCreateLoading: boolean;

  mappings: TemplateMapping[];
  mappingsLoading: boolean;
  selectedMapping: TemplateMapping | null;
  selectedMappingLoading: boolean;
  mappingCreateLoading: boolean;

  mappingContents: TemplateMappingContent[];
  mappingContentsLoading: boolean;
  selectedMappingContent: TemplateMappingContent | null;
  selectedMappingContentLoading: boolean;
};

const initialState: TemplatesState = {
  templates: [],
  isLoading: false,
  templateCreateLoading: false,

  selectedTemplate: null,
  selectedTemplateLoading: false,

  additionalReceivers: [],
  additionalReceiversLoading: false,
  selectedAdditionalReceiver: null,
  selectedAdditionalReceiverLoading: false,
  additionalReceiverCreateLoading: false,

  mappings: [],
  mappingsLoading: false,
  selectedMapping: null,
  selectedMappingLoading: false,
  mappingCreateLoading: false,

  mappingContents: [],
  mappingContentsLoading: false,
  selectedMappingContent: null,
  selectedMappingContentLoading: false,
};

const templatesDictionary: StateDictionary<TemplateListItem> = {
  entityList: 'templates',
  listLoading: 'isLoading',
  selectedEntity: 'selectedTemplate',
  selectedEntityLoading: 'selectedTemplateLoading',
  entityId: 'id',
  createLoading: 'templateCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const TemplatesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('templates-store'),

  withComputed(state => ({
    selectedTemplateId: computed(() => state.selectedTemplate()?.id || null),
    selectedAdditionalReceiverId: computed(() => state.selectedAdditionalReceiver()?.id || null),
    selectedMappingId: computed(() => state.selectedMapping()?.id || null),
    selectedMappingContentName: computed(() => state.selectedMappingContent()?.name || null),
  })),

  withMethods((store, templatesService = inject(TemplatesService), router = inject(Router)) => {
    createStateResetHandler<TemplatesState>('/notifications/templates')(store, initialState);
    createCustomStateSetOnUrlHandler<TemplatesState>('/notifications/templates', {
      selectedTemplate: null,
      selectedAdditionalReceiver: null,
      selectedMapping: null,
      selectedMappingContent: null,
    })(store);

    createCustomStateSetOnRegexHandler<TemplatesState>(new RegExp(`^/notifications/templates/${guidRegex}/receivers$`))(store, {
      selectedAdditionalReceiver: null,
    });
    createCustomStateSetOnRegexHandler<TemplatesState>(new RegExp(`^/notifications/templates/${guidRegex}/mappings$`))(store, {
      selectedMapping: null,
    });
    const templateMethods = {
      getAll: getAll<TemplateListItem>({
        store,
        service: templatesService,
        dictionary: templatesDictionary,
      }),
      getOne: getOne<Template>({
        store,
        service: templatesService,
        dictionary: templatesDictionary,
      }),
      add: addEntity<Template, CreateTemplateRequest>({
        store,
        service: templatesService,
        dictionary: templatesDictionary,
        onSuccessCallback: newTemplate => {
          router.navigate(['notifications', 'templates', newTemplate.id, 'details']);
        },
      }),
      update: updateEntity<Template, UpdateTemplateRequest>({
        store,
        service: templatesService,
        dictionary: templatesDictionary,
      }),
      delete: deleteEntity<Template>({
        store,
        service: templatesService,
        dictionary: templatesDictionary,
        onSuccessCallback: () => {
          router.navigate(['notifications', 'templates']);
          templateMethods.getAll();
        },
      }),
    };

    // Additional Receivers methods
    const additionalReceiverMethods = {
      getAllAdditionalReceivers: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { additionalReceivers: [], additionalReceiversLoading: true })),
          switchMap(templateId =>
            templatesService.getAllAdditionalReceivers(templateId).pipe(
              tap(additionalReceivers => {
                patchState(store, {
                  additionalReceiversLoading: false,
                  additionalReceivers: sortAlphabetically([...additionalReceivers], (a, b) => a.groupName.localeCompare(b.groupName)),
                });
              }),
              catchError(() => {
                patchState(store, { additionalReceiversLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      addAdditionalReceiver: rxMethod<{ templateId: string; additionalReceiver: CreateTemplateAdditionalReceiverRequest }>(
        pipe(
          tap(() => patchState(store, { additionalReceiversLoading: true, additionalReceiverCreateLoading: true })),
          switchMap(({ templateId, additionalReceiver }) =>
            templatesService.createAdditionalReceiver(templateId, additionalReceiver).pipe(
              tap(additionalReceiver => {
                patchState(store, state => ({
                  additionalReceiverCreateLoading: false,
                  additionalReceiversLoading: false,
                  additionalReceivers: sortAlphabetically([...state.additionalReceivers, additionalReceiver], (a, b) =>
                    a.groupName.localeCompare(b.groupName),
                  ),
                  selectedAdditionalReceiver: additionalReceiver,
                }));
                router.navigate(['notifications', 'templates', templateId, 'receivers', additionalReceiver.id, 'details']);
              }),
              catchError(() => {
                patchState(store, { additionalReceiverCreateLoading: false, additionalReceiversLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      updateAdditionalReceiver: rxMethod<{
        templateId: string;
        additionalReceiverId: string;
        additionalReceiver: UpdateTemplateAdditionalReceiverRequest;
      }>(
        pipe(
          tap(() => patchState(store, { selectedAdditionalReceiverLoading: true, additionalReceiversLoading: true })),
          switchMap(({ templateId, additionalReceiverId, additionalReceiver }) =>
            templatesService.updateAdditionalReceiver(templateId, additionalReceiverId, additionalReceiver).pipe(
              tap(additionalReceiver => {
                patchState(store, state => ({
                  additionalReceiversLoading: false,
                  selectedAdditionalReceiverLoading: false,
                  selectedAdditionalReceiver: additionalReceiver,
                  additionalReceivers: sortAlphabetically(
                    findAndReplace(state.additionalReceivers, additionalReceiver, item => item.id === additionalReceiver.id),
                    (a, b) => a.groupName.localeCompare(b.groupName),
                  ),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedAdditionalReceiver: { ...state.selectedAdditionalReceiver! },
                  additionalReceiversLoading: false,
                  selectedAdditionalReceiverLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      deleteAdditionalReceiver: rxMethod<{ templateId: string; additionalReceiverId: string }>(
        pipe(
          tap(() => patchState(store, { additionalReceiversLoading: true })),
          switchMap(({ templateId, additionalReceiverId }) =>
            templatesService.deleteAdditionalReceiver(templateId, additionalReceiverId).pipe(
              tap(() => {
                patchState(store, {
                  additionalReceiversLoading: false,
                  selectedAdditionalReceiver: null,
                });
                router.navigate(['notifications', 'templates', templateId, 'receivers']);
                additionalReceiverMethods.getAllAdditionalReceivers(templateId);
              }),
              catchError(() => {
                patchState(store, { additionalReceiversLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      selectAdditionalReceiver: rxMethod<{ templateId: string; additionalReceiverId: string }>(
        pipe(
          tap(() => patchState(store, { selectedAdditionalReceiverLoading: true })),
          switchMap(({ templateId, additionalReceiverId }) =>
            templatesService.getOneAdditionalReceiver(templateId, additionalReceiverId).pipe(
              tap(additionalReceiver =>
                patchState(store, { selectedAdditionalReceiverLoading: false, selectedAdditionalReceiver: additionalReceiver }),
              ),
              catchError(() => {
                patchState(store, { selectedAdditionalReceiverLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    // Mappings methods
    const mappingMethods = {
      getAllMappings: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { mappings: [], mappingsLoading: true })),
          switchMap(templateId =>
            templatesService.getAllMappings(templateId).pipe(
              tap(mappings => patchState(store, { mappingsLoading: false, mappings })),
              catchError(() => {
                patchState(store, { mappingsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      addMapping: rxMethod<{ templateId: string; mapping: CreateTemplateMappingRequest }>(
        pipe(
          tap(() => patchState(store, { mappingsLoading: true, mappingCreateLoading: true })),
          switchMap(({ templateId, mapping }) =>
            templatesService.createMapping(templateId, mapping).pipe(
              tap(mapping => {
                patchState(store, state => ({
                  mappingCreateLoading: false,
                  mappingsLoading: false,
                  mappings: [...state.mappings, mapping],
                  selectedMapping: mapping,
                }));
                router.navigate(['notifications', 'templates', templateId, 'mappings', mapping.id, 'details']);
              }),
              catchError(() => {
                patchState(store, { mappingCreateLoading: false, mappingsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      updateMapping: rxMethod<{ templateId: string; mappingId: string; mapping: UpdateTemplateMappingRequest }>(
        pipe(
          tap(() => patchState(store, { selectedMappingLoading: true, mappingsLoading: true })),
          switchMap(({ templateId, mappingId, mapping }) =>
            templatesService.updateMapping(templateId, mappingId, mapping).pipe(
              tap(mapping => {
                patchState(store, state => ({
                  mappingsLoading: false,
                  selectedMappingLoading: false,
                  selectedMapping: mapping,
                  mappings: findAndReplace(state.mappings, mapping, item => item.id === mapping.id),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedMapping: { ...state.selectedMapping! },
                  mappingsLoading: false,
                  selectedMappingLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      deleteMapping: rxMethod<{ templateId: string; mappingId: string }>(
        pipe(
          tap(() => patchState(store, { mappingsLoading: true })),
          switchMap(({ templateId, mappingId }) =>
            templatesService.deleteMapping(templateId, mappingId).pipe(
              tap(() => {
                patchState(store, {
                  mappingsLoading: false,
                  selectedMapping: null,
                });
                router.navigate(['notifications', 'templates', templateId, 'mappings']);
                mappingMethods.getAllMappings(templateId);
              }),
              catchError(() => {
                patchState(store, { mappingsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      selectMapping: rxMethod<{ templateId: string; mappingId: string }>(
        pipe(
          tap(() => patchState(store, { selectedMappingLoading: true })),
          switchMap(({ templateId, mappingId }) =>
            templatesService.getOneMapping(templateId, mappingId).pipe(
              tap(mapping => patchState(store, { selectedMappingLoading: false, selectedMapping: mapping })),
              catchError(() => {
                patchState(store, { selectedMappingLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      mappingActivation: rxMethod<{ templateId: string; mappingId: string; activate: boolean }>(
        pipe(
          tap(() => patchState(store, { selectedMappingLoading: true })),
          switchMap(({ templateId, mappingId, activate }) =>
            templatesService.mappingActivation(templateId, mappingId, activate).pipe(
              tap(mapping => {
                patchState(store, state => ({
                  selectedMappingLoading: false,
                  selectedMapping: mapping,
                  mappings: findAndReplace(state.mappings, mapping, item => item.id === mapping.id),
                }));
              }),
              catchError(() => {
                patchState(store, { selectedMappingLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    // Mapping Contents methods
    const mappingContentMethods = {
      getAllMappingContents: rxMethod<{ templateId: string; mappingId: string }>(
        pipe(
          tap(() => patchState(store, { mappingContents: [], mappingContentsLoading: true })),
          switchMap(({ templateId, mappingId }) =>
            templatesService.getAllMappingContents(templateId, mappingId).pipe(
              tap(contents => patchState(store, { mappingContentsLoading: false, mappingContents: contents })),
              catchError(() => {
                patchState(store, { mappingContentsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      updateMappingContent: rxMethod<{
        templateId: string;
        mappingId: string;
        tagName: string;
        content: UpdateTemplateMappingContentRequest;
      }>(
        pipe(
          tap(() => patchState(store, { selectedMappingContentLoading: true, mappingContentsLoading: true })),
          switchMap(({ templateId, mappingId, tagName, content }) =>
            templatesService.updateMappingContent(templateId, mappingId, tagName, content).pipe(
              tap(content => {
                patchState(store, state => ({
                  mappingContentsLoading: false,
                  selectedMappingContentLoading: false,
                  selectedMappingContent: content,
                  mappingContents: findAndReplace(state.mappingContents, content, item => item.name === content.name),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedMappingContent: { ...state.selectedMappingContent! },
                  mappingContentsLoading: false,
                  selectedMappingContentLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      deleteMappingContent: rxMethod<{ templateId: string; mappingId: string; tagName: string }>(
        pipe(
          tap(() => patchState(store, { mappingContentsLoading: true })),
          switchMap(({ templateId, mappingId, tagName }) =>
            templatesService.deleteMappingContent(templateId, mappingId, tagName).pipe(
              tap(() => {
                patchState(store, {
                  mappingContentsLoading: false,
                  selectedMappingContent: null,
                });
                router.navigate(['notifications', 'templates', templateId, 'mappings', mappingId, 'content']);
                mappingContentMethods.getAllMappingContents({ templateId, mappingId });
              }),
              catchError(() => {
                patchState(store, { mappingContentsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      selectMappingContent: rxMethod<{ templateId: string; mappingId: string; tagName: string }>(
        pipe(
          tap(() => patchState(store, { selectedMappingContentLoading: true })),
          switchMap(({ templateId, mappingId, tagName }) =>
            templatesService.getOneMappingContent(templateId, mappingId, tagName).pipe(
              tap(content => patchState(store, { selectedMappingContentLoading: false, selectedMappingContent: content })),
              catchError(() => {
                patchState(store, { selectedMappingContentLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    // Navigation methods
    const navigationActions = {
      goToTemplateActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'templates', id]);

        if (!result) return false;

        if (!store.selectedTemplateLoading()) templateMethods.getOne(id);

        return true;
      },
      goToTemplateListPage: (): void => {
        patchState(store, { selectedTemplate: null });
        router.navigate(['notifications', 'templates']);
      },
      goToAdditionalReceiverActions: async (templateId: string, additionalReceiverId: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'templates', templateId, 'receivers', additionalReceiverId]);

        if (!result) return false;

        if (!store.selectedAdditionalReceiverLoading())
          additionalReceiverMethods.selectAdditionalReceiver({ templateId, additionalReceiverId });

        return true;
      },
      goToAdditionalReceiverListPage: (templateId: string): void => {
        patchState(store, { selectedAdditionalReceiver: null });
        router.navigate(['notifications', 'templates', templateId, 'receivers']);
      },
      goToMappingActions: async (templateId: string, mappingId: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'templates', templateId, 'mappings', mappingId]);

        if (!result) return false;

        if (!store.selectedMappingLoading()) {
          mappingMethods.selectMapping({ templateId, mappingId });
          mappingContentMethods.getAllMappingContents({
            templateId,
            mappingId,
          });
        }

        return true;
      },
      goToMappingListPage: (templateId: string): void => {
        patchState(store, { selectedMapping: null });
        router.navigate(['notifications', 'templates', templateId, 'mappings']);
      },
      goToMappingContentActions: async (templateId: string, mappingId: string, tagName: string): Promise<boolean> => {
        const result = await router.navigate(['notifications', 'templates', templateId, 'mappings', mappingId, 'content', tagName]);

        if (!result) return false;

        if (!store.selectedMappingContentLoading()) mappingContentMethods.selectMappingContent({ templateId, mappingId, tagName });

        return true;
      },
      goToMappingContentListPage: (templateId: string, mappingId: string): void => {
        patchState(store, { selectedMappingContent: null });
        router.navigate(['notifications', 'templates', templateId, 'mappings', mappingId, 'content']);
      },
    };

    return {
      ...templateMethods,
      ...additionalReceiverMethods,
      ...mappingMethods,
      ...mappingContentMethods,
      ...navigationActions,
    };
  }),
);

export interface TemplatesStoreType {
  // state signals
  readonly templates: Signal<TemplateListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly templateCreateLoading: Signal<boolean>;
  readonly selectedTemplate: Signal<Template | null>;
  readonly selectedTemplateLoading: Signal<boolean>;
  readonly additionalReceivers: Signal<TemplateAdditionalReceiver[]>;
  readonly additionalReceiversLoading: Signal<boolean>;
  readonly selectedAdditionalReceiver: Signal<TemplateAdditionalReceiver | null>;
  readonly selectedAdditionalReceiverLoading: Signal<boolean>;
  readonly additionalReceiverCreateLoading: Signal<boolean>;
  readonly mappings: Signal<TemplateMapping[]>;
  readonly mappingsLoading: Signal<boolean>;
  readonly selectedMapping: Signal<TemplateMapping | null>;
  readonly selectedMappingLoading: Signal<boolean>;
  readonly mappingCreateLoading: Signal<boolean>;
  readonly mappingContents: Signal<TemplateMappingContent[]>;
  readonly mappingContentsLoading: Signal<boolean>;
  readonly selectedMappingContent: Signal<TemplateMappingContent | null>;
  readonly selectedMappingContentLoading: Signal<boolean>;

  // computed signals
  readonly selectedTemplateId: Signal<string | null>;
  readonly selectedAdditionalReceiverId: Signal<string | null>;
  readonly selectedMappingId: Signal<string | null>;
  readonly selectedMappingContentName: Signal<string | null>;

  // template methods
  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly add: (request: CreateTemplateRequest) => void;
  readonly update: (input: { id: string; dto: UpdateTemplateRequest }) => void;
  readonly delete: (id: string) => void;

  // additional receiver methods
  readonly getAllAdditionalReceivers: (templateId: string) => void;
  readonly addAdditionalReceiver: (input: { templateId: string; additionalReceiver: CreateTemplateAdditionalReceiverRequest }) => void;
  readonly updateAdditionalReceiver: (input: {
    templateId: string;
    additionalReceiverId: string;
    additionalReceiver: UpdateTemplateAdditionalReceiverRequest;
  }) => void;
  readonly deleteAdditionalReceiver: (input: { templateId: string; additionalReceiverId: string }) => void;
  readonly selectAdditionalReceiver: (input: { templateId: string; additionalReceiverId: string }) => void;

  // mapping methods
  readonly getAllMappings: (templateId: string) => void;
  readonly addMapping: (input: { templateId: string; mapping: CreateTemplateMappingRequest }) => void;
  readonly updateMapping: (input: { templateId: string; mappingId: string; mapping: UpdateTemplateMappingRequest }) => void;
  readonly deleteMapping: (input: { templateId: string; mappingId: string }) => void;
  readonly selectMapping: (input: { templateId: string; mappingId: string }) => void;
  readonly mappingActivation: (input: { templateId: string; mappingId: string; activate: boolean }) => void;

  // mapping content methods
  readonly getAllMappingContents: (input: { templateId: string; mappingId: string }) => void;
  readonly updateMappingContent: (input: {
    templateId: string;
    mappingId: string;
    tagName: string;
    content: UpdateTemplateMappingContentRequest;
  }) => void;
  readonly deleteMappingContent: (input: { templateId: string; mappingId: string; tagName: string }) => void;
  readonly selectMappingContent: (input: { templateId: string; mappingId: string; tagName: string }) => void;

  // navigation methods
  readonly goToTemplateActions: (id: string) => Promise<boolean>;
  readonly goToTemplateListPage: () => void;
  readonly goToAdditionalReceiverActions: (templateId: string, additionalReceiverId: string) => Promise<boolean>;
  readonly goToAdditionalReceiverListPage: (templateId: string) => void;
  readonly goToMappingActions: (templateId: string, mappingId: string) => Promise<boolean>;
  readonly goToMappingListPage: (templateId: string) => void;
  readonly goToMappingContentActions: (templateId: string, mappingId: string, tagName: string) => Promise<boolean>;
  readonly goToMappingContentListPage: (templateId: string, mappingId: string) => void;
}
